import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  MonitorSmartphone,
  Plus,
  RefreshCw,
  UploadCloud,
  PlayCircle,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
} from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import UploadStorageStateDialog from "../components/UploadStorageStateDialog";
import VerifySessionDialog from "../components/VerifySessionDialog";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

const HEALTH_STYLES = {
  ok: {
    label: "Operativo",
    dot: "bg-emerald-500",
    text: "text-emerald-400",
    Icon: CheckCircle2,
  },
  krai_api_not_configured: {
    label: "KRAI API sin configurar",
    dot: "bg-zinc-500",
    text: "text-zinc-400",
    Icon: AlertTriangle,
  },
  krai_api_unreachable: {
    label: "KRAI API inaccesible",
    dot: "bg-red-500",
    text: "text-red-400",
    Icon: XCircle,
  },
  krai_api_error: {
    label: "KRAI API con error",
    dot: "bg-red-500",
    text: "text-red-400",
    Icon: XCircle,
  },
  playwright_unreachable: {
    label: "Playwright inaccesible",
    dot: "bg-amber-500",
    text: "text-amber-400",
    Icon: AlertTriangle,
  },
  default: {
    label: "Desconocido",
    dot: "bg-zinc-500",
    text: "text-zinc-400",
    Icon: AlertTriangle,
  },
};

function formatBytes(n) {
  if (typeof n !== "number") return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BrowserPage() {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState("");
  const [profilesLoading, setProfilesLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);

  const [uploadFor, setUploadFor] = useState(null);
  const [verifyFor, setVerifyFor] = useState(null);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/browser/health");
      setHealth(data);
    } catch (e) {
      setHealth({ ok: false, code: "krai_api_error", message: apiErrorMessage(e) });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setProfilesLoading(true);
    setProfilesError("");
    try {
      const { data } = await api.get("/browser/profiles");
      setProfiles(Array.isArray(data) ? data : data?.profiles || []);
    } catch (e) {
      setProfilesError(apiErrorMessage(e));
      setProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    if (health?.ok) loadProfiles();
  }, [health?.ok, loadProfiles]);

  async function createProfile(e) {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    try {
      await api.post("/browser/profiles", { name: createName.trim() });
      toast.success("Perfil creado");
      setCreateName("");
      setCreateOpen(false);
      loadProfiles();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  async function deleteProfile(name) {
    if (!window.confirm(`¿Eliminar el perfil "${name}" y su storage_state?`)) return;
    try {
      await api.delete(`/browser/profiles/${encodeURIComponent(name)}`);
      toast.success("Perfil eliminado");
      loadProfiles();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  const style =
    HEALTH_STYLES[health?.code] ||
    (health?.ok ? HEALTH_STYLES.ok : HEALTH_STYLES.default);
  const HealthIcon = style.Icon;
  const isOperational = Boolean(health?.ok);

  return (
    <div data-testid={TID.browser.root}>
      <PageHeader
        eyebrow="Automatización"
        title="Navegador / Playwright"
        description="Chromium remoto persistente vía KRAI API. KRAI CONNECT no habla directamente con el navegador."
        actions={
          <Button
            data-testid={TID.browser.refreshHealth}
            onClick={loadHealth}
            variant="secondary"
            disabled={healthLoading}
            className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 h-9"
          >
            <RefreshCw
              className={`h-4 w-4 mr-1.5 ${healthLoading ? "animate-spin" : ""}`}
            />
            Comprobar estado
          </Button>
        }
      />

      {/* Health panel */}
      <div
        data-testid={TID.browser.healthBadge}
        className="rounded-md border border-zinc-800 bg-zinc-950 p-5 mb-6"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md border border-zinc-800 bg-zinc-900 grid place-items-center">
              <MonitorSmartphone className="h-5 w-5 text-zinc-300" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                Cadena KRAI CONNECT → KRAI API → Playwright
              </div>
              <div className={`inline-flex items-center gap-2 mt-1 ${style.text}`}>
                <span className={`krai-dot ${style.dot}`} />
                <HealthIcon className="h-4 w-4" />
                <span className="font-semibold">{style.label}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1 max-w-2xl">
                {health?.message || "Sin datos aún"}
              </div>
            </div>
          </div>
          <div className="text-right text-[11px] font-mono text-zinc-500 shrink-0 space-y-0.5">
            <div>fuente: {health?.source || "—"}</div>
            {health?.playwright_version && <div>pw: {health.playwright_version}</div>}
            {typeof health?.latency_ms === "number" && (
              <div>latencia: {health.latency_ms} ms</div>
            )}
          </div>
        </div>

        {health?.code === "krai_api_not_configured" && (
          <div className="mt-4 text-xs text-zinc-400">
            Ve a{" "}
            <Link
              to="/krai-api"
              className="underline underline-offset-2 hover:text-white"
            >
              KRAI API
            </Link>{" "}
            y añade la URL base y el token.
          </div>
        )}
      </div>

      {/* Profiles */}
      {!isOperational ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Los perfiles se cargarán cuando la cadena esté operativa.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              Perfiles de navegador
            </div>
            <Button
              data-testid={TID.browser.createProfile}
              onClick={() => setCreateOpen(true)}
              className="bg-white text-black hover:bg-zinc-200 h-8"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Crear perfil
            </Button>
          </div>

          {profilesError && (
            <div className="rounded-md border border-red-900/40 bg-red-950/20 text-red-300 text-xs p-3 mb-4">
              {profilesError}
            </div>
          )}

          {profilesLoading ? (
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-8 text-sm text-zinc-500">
              Cargando perfiles…
            </div>
          ) : profiles.length === 0 ? (
            <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
              Todavía no hay perfiles. Crea uno para empezar (p. ej. "gemini").
            </div>
          ) : (
            <div className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800">
                <div className="col-span-3">Perfil</div>
                <div className="col-span-2">storage_state</div>
                <div className="col-span-2">Tamaño</div>
                <div className="col-span-2">Actualizado</div>
                <div className="col-span-3 text-right">Acciones</div>
              </div>
              {profiles.map((p) => (
                <ProfileRow
                  key={p.name}
                  profile={p}
                  onUpload={() => setUploadFor(p.name)}
                  onVerify={() => setVerifyFor(p.name)}
                  onDelete={() => deleteProfile(p.name)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create profile dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          data-testid={TID.browser.createDialog}
          className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[440px]"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Crear perfil</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Un perfil agrupa el storage_state de un servicio (cookies + localStorage).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createProfile} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nombre (a-z, 0-9, - o _)</Label>
              <Input
                data-testid={TID.browser.createName}
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="gemini"
                pattern="[a-zA-Z0-9_\-]+"
                maxLength={64}
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCreateOpen(false)}
                className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid={TID.browser.createSubmit}
                disabled={creating || !createName.trim()}
                className="bg-white text-black hover:bg-zinc-200"
              >
                {creating ? "Creando…" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload storage_state dialog */}
      {uploadFor && (
        <UploadStorageStateDialog
          open={Boolean(uploadFor)}
          onOpenChange={(v) => !v && setUploadFor(null)}
          profileName={uploadFor}
          onUploaded={loadProfiles}
        />
      )}

      {/* Verify session dialog */}
      {verifyFor && (
        <VerifySessionDialog
          open={Boolean(verifyFor)}
          onOpenChange={(v) => !v && setVerifyFor(null)}
          profileName={verifyFor}
        />
      )}
    </div>
  );
}

function ProfileRow({ profile, onUpload, onVerify, onDelete }) {
  const hasState = Boolean(profile.has_storage_state);
  return (
    <div
      data-testid={TID.browser.profileRow(profile.name)}
      className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-zinc-900 last:border-0 hover:bg-zinc-900/40"
    >
      <div className="col-span-3 font-mono text-sm text-white truncate">
        {profile.name}
      </div>
      <div className="col-span-2">
        {hasState ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
            <CheckCircle2 className="h-3 w-3" /> presente
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-400">
            <AlertTriangle className="h-3 w-3" /> vacío
          </span>
        )}
      </div>
      <div className="col-span-2 text-xs font-mono text-zinc-400">
        {formatBytes(profile.size_bytes)}
      </div>
      <div className="col-span-2 text-xs font-mono text-zinc-400">
        {formatDate(profile.updated_at)}
      </div>
      <div className="col-span-3 flex items-center justify-end gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.browser.uploadBtn(profile.name)}
          onClick={onUpload}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
          title="Subir storage_state"
        >
          <UploadCloud className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          data-testid={TID.browser.verifyBtn(profile.name)}
          onClick={onVerify}
          disabled={!hasState}
          className="h-8 bg-white text-black hover:bg-zinc-200 disabled:opacity-40"
          title={hasState ? "Verificar sesión" : "Sube antes un storage_state"}
        >
          <PlayCircle className="h-3.5 w-3.5 mr-1" />
          Verificar
        </Button>
        <button
          data-testid={TID.browser.deleteBtn(profile.name)}
          onClick={onDelete}
          className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors duration-150"
          title="Eliminar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
